import { useVirtualizer, VirtualItem } from "@tanstack/react-virtual";
import { memo, RefObject, useCallback, useLayoutEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import useRowKeyboardNavigation from "../Table/hooks/useKeyboardNavigation/useRowKeyboardNavigation";
import { calculateVirtualPadding } from "../Table/hooks/useStickyRow/helpers";
import { selectVisibleColumns } from "../Table/store/useTableStore/selectors";
import { OVERSCAN, ROW_HEIGHT_ESTIMATE } from "./constants";
import { getItemId } from "./helpers";
import useTableSimpleEffectiveColumnPinning from "./hooks/useEffectiveColumnPinning";
import TableSimpleRow from "./Row";
import { TableSimpleDataItem, TableSimpleRowsBaseProps } from "./types";

type TableSimpleVirtualizedRowsProps<T extends TableSimpleDataItem> =
  TableSimpleRowsBaseProps<T> & {
    tableRef: RefObject<HTMLDivElement | null>;
  };

const TableSimpleVirtualizedRows = <T extends TableSimpleDataItem>({
  tableRef,
  data,
  store,
  selectionStore,
  hasMoreSelectionOptions,
  shiftSelectionAction,
  cellPerColumn,
  itemIdKey = "id",
  stickyRight,
  activeRowId,
}: TableSimpleVirtualizedRowsProps<T>) => {
  const keyboardProps = useRowKeyboardNavigation({ isGroup: false });

  const { columns, isFirstColumnPinned } = store(
    useShallow((state) => ({
      columns: selectVisibleColumns(state),
      isFirstColumnPinned: state.isFirstColumnPinned,
    }))
  );

  const { effectiveIsFirstColumnPinned } = useTableSimpleEffectiveColumnPinning(
    tableRef,
    isFirstColumnPinned
  );

  const getItemKey = useCallback(
    (index: number) => getItemId(data[index], itemIdKey),
    [data, itemIdKey]
  );

  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => tableRef.current,
    estimateSize: () => ROW_HEIGHT_ESTIMATE,
    overscan: OVERSCAN,
    getItemKey,
  });

  useLayoutEffect(() => {
    virtualizer.measure();
  }, [data.length, virtualizer]);

  const virtualizedItems = virtualizer.getVirtualItems();
  const [topGap, bottomGap] = calculateVirtualPadding(virtualizedItems, virtualizer.getTotalSize());

  const renderRow = useCallback(
    (virtualRow: VirtualItem) => {
      const item = data[virtualRow.index];
      const itemId = getItemId(item, itemIdKey);

      return (
        <TableSimpleRow
          key={virtualRow.key}
          itemId={itemId}
          item={item}
          columns={columns}
          isFirstColumnPinned={effectiveIsFirstColumnPinned}
          selectionStore={selectionStore}
          hasMoreSelectionOptions={hasMoreSelectionOptions}
          shiftSelectionAction={shiftSelectionAction}
          cellPerColumn={cellPerColumn}
          keyboardProps={keyboardProps}
          measureRef={virtualizer.measureElement}
          dataIndex={virtualRow.index}
          virtualized
          stickyRight={stickyRight}
          isActive={itemId === activeRowId}
        />
      );
    },
    [
      data,
      itemIdKey,
      columns,
      effectiveIsFirstColumnPinned,
      selectionStore,
      hasMoreSelectionOptions,
      shiftSelectionAction,
      cellPerColumn,
      keyboardProps,
      virtualizer.measureElement,
      stickyRight,
      activeRowId,
    ]
  );

  return (
    <>
      {topGap > 0 && <div style={{ display: "table-row", height: topGap }} aria-hidden="true" />}
      {virtualizedItems.map(renderRow)}
      {bottomGap > 0 && (
        <div style={{ display: "table-row", height: bottomGap }} aria-hidden="true" />
      )}
    </>
  );
};

TableSimpleVirtualizedRows.displayName = "DS.TableSimple.VirtualizedRows";

export default memo(TableSimpleVirtualizedRows) as typeof TableSimpleVirtualizedRows;
